import { Check } from 'lucide-react'
import type { ColorScheme } from '@/types'
import { COLOR_SCHEMES, COLOR_SCHEME_METADATA } from '@/themes/themeConfig'

interface ColorSchemeSelectorProps {
  currentScheme: ColorScheme
  onChange: (scheme: ColorScheme) => void
}

export default function ColorSchemeSelector({
  currentScheme,
  onChange,
}: ColorSchemeSelectorProps) {
  const schemes: ColorScheme[] = ['blue', 'purple', 'green', 'orange', 'dark', 'red', 'teal', 'indigo', 'rose', 'emerald']

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Color Scheme
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Select a color scheme to personalize your profile.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {schemes.map((scheme) => {
          const colors = COLOR_SCHEMES[scheme]
          const metadata = COLOR_SCHEME_METADATA[scheme]
          const isSelected = currentScheme === scheme

          return (
            <button
              key={scheme}
              onClick={() => onChange(scheme)}
              className={`
                relative p-3 rounded-lg border-2 transition-all
                ${
                  isSelected
                    ? 'border-gray-900 bg-gray-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }
              `}
              aria-label={`Select ${metadata.name} color scheme`}
            >
              {/* Color circle */}
              <div className="relative flex justify-center mb-2">
                <div
                  className="w-12 h-12 rounded-full border-2 border-white shadow-md"
                  style={{ backgroundColor: colors.primary }}
                />
                {isSelected && (
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                    <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center">
                      <Check className="w-4 h-4" style={{ color: colors.primary }} />
                    </div>
                  </div>
                )}
              </div>

              {/* Label */}
              <p className="text-sm font-medium text-gray-900 text-center">
                {metadata.name}
              </p>
              <p className="text-xs text-gray-600 text-center mt-1">
                {metadata.description}
              </p>
            </button>
          )
        })}
      </div>
    </div>
  )
}
